import type { MemberManagementData } from '../member/model';
import type { Notification } from './model';
import type { NotificationListItem } from './view';

interface NotificationRulePorts {
  readonly now: () => string;
  readonly newId: (prefix: string) => string;
}

export function createSongScoreUpdatedNotification(
  data: MemberManagementData,
  input: {
    readonly choirId: string;
    readonly seasonId: string;
    readonly actorSeasonMembershipId: string;
    readonly songId: string;
    readonly songName: string;
    readonly updateNote: string | null;
    readonly ports: NotificationRulePorts;
  }
): { readonly data: MemberManagementData; readonly notification: Notification } {
  const notification: Notification = {
    notificationId: input.ports.newId('notification'),
    choirId: input.choirId,
    seasonId: input.seasonId,
    type: 'song_score_updated',
    targetType: 'song',
    targetId: input.songId,
    title: '当前乐谱已更新',
    body: input.songName,
    updateNote: input.updateNote,
    actorSeasonMembershipId: input.actorSeasonMembershipId,
    createdAt: input.ports.now()
  };

  return {
    data: {
      ...data,
      notifications: [...data.notifications, notification]
    },
    notification
  };
}

export function markNotificationRead(
  data: MemberManagementData,
  input: {
    readonly notificationId: string;
    readonly seasonMembershipId: string;
    readonly now: string;
  }
): { readonly data: MemberManagementData } {
  const notification = data.notifications.find((candidate) => candidate.notificationId === input.notificationId);
  if (!notification) {
    throw new Error('Notification not found');
  }

  assertSeasonMembershipInScope(data, {
    seasonMembershipId: input.seasonMembershipId,
    choirId: notification.choirId,
    seasonId: notification.seasonId
  });

  const alreadyRead = data.notificationReads.some(
    (read) => read.notificationId === input.notificationId && read.seasonMembershipId === input.seasonMembershipId
  );
  if (alreadyRead) {
    return { data };
  }

  return {
    data: {
      ...data,
      notificationReads: [
        ...data.notificationReads,
        {
          notificationId: input.notificationId,
          seasonMembershipId: input.seasonMembershipId,
          readAt: input.now
        }
      ]
    }
  };
}

export function listNotificationsForMember(
  data: MemberManagementData,
  input: {
    readonly choirId: string;
    readonly seasonId: string;
    readonly seasonMembershipId: string;
  }
): readonly NotificationListItem[] {
  assertSeasonMembershipInScope(data, input);

  return data.notifications
    .filter((notification) => notification.choirId === input.choirId && notification.seasonId === input.seasonId)
    .map((notification) => {
      const readAt =
        data.notificationReads.find(
          (read) =>
            read.notificationId === notification.notificationId && read.seasonMembershipId === input.seasonMembershipId
        )?.readAt ?? null;

      return {
        notificationId: notification.notificationId,
        type: notification.type,
        title: notification.title,
        body: notification.body,
        updateNote: notification.updateNote,
        actorSeasonMembershipId: notification.actorSeasonMembershipId,
        createdAt: notification.createdAt,
        readAt,
        targetType: notification.targetType,
        targetId: notification.targetId
      };
    })
    .sort(compareNotificationListItems);
}

function compareNotificationListItems(left: NotificationListItem, right: NotificationListItem): number {
  return right.createdAt.localeCompare(left.createdAt);
}

function assertSeasonMembershipInScope(
  data: MemberManagementData,
  input: {
    readonly seasonMembershipId: string;
    readonly choirId: string;
    readonly seasonId: string;
  }
): void {
  const seasonMembership = data.seasonMemberships.find(
    (candidate) => candidate.seasonMembershipId === input.seasonMembershipId
  );
  if (!seasonMembership) {
    throw new Error('Season membership not found');
  }
  if (seasonMembership.choirId !== input.choirId || seasonMembership.seasonId !== input.seasonId) {
    throw new Error('Season membership does not match notification');
  }
}
